import { getUser } from 'auth';
import Spinner from 'components-stateless/Spinner';
import { allPermissions as userPermissions } from 'constants/userPermissions';
import { selectIsTicketingFeatureEnabled } from 'containers/Home/selectors';
import type { Board } from 'containers/Ticketing/_api/types';
import { useGetBoardsQuery } from 'containers/Ticketing/_api/useGetBoardsQuery';
import { useGetBoardViewsQuery } from 'containers/Ticketing/_api/useGetBoardViewsQuery';
import { useIsRequesterView } from 'containers/Ticketing/RequesterPortal/hooks/useIsRequesterView';
import { useStore } from 'hooks/useStore';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { addRecord, getAllRecords } from 'utils/localStorageManager';

const ONBOARDING_URL = '/app/ticketing/onboarding';
const ALL_BOARDS_URL = '/app/ticketing/boards/all';

const isAdmin = () => {
  const user = getUser();
  return user && user.roleName === 'Administrator';
};

const isTicketingAdmin = () => {
  const user = getUser();
  return (
    user &&
    user.permissions?.includes(userPermissions.matterAdminPermissions.code)
  );
};

const isAgent = () => {
  const user = getUser();
  return (
    user &&
    user.permissions?.includes(userPermissions.matterAgentPermissions.code)
  );
};

const useFetchBoardsAndViews = ({
  isTicketPath = false,
}: { isTicketPath?: boolean } = {}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Allow requester and participants to redirect to the requester portal
  const isRequesterView = useIsRequesterView();

  const addNotificationError = useStore(
    (state) => state.notificationSlice.addNotificationError
  );

  const { data: boardsResponse, isLoading: boardsLoading } = useGetBoardsQuery({
    onError: (error) => {
      if (isTicketPath || isRequesterView) {
        return;
      }
      const errMessage = 'Error fetching boards during ticketing route';
      addNotificationError({
        message: errMessage,
      });
      navigate('/app', { replace: true });
    },
  });

  const boardIdParams = id || boardsResponse?.result?.[0]?.id || '';

  const { data: viewsResponse, isFetching: viewsFetching } =
    useGetBoardViewsQuery({
      id: boardIdParams,
      onError: (error) => {
        if (isTicketPath || isRequesterView) {
          return;
        }
        const errMessage = 'Error fetching views during ticketing route';
        console.error(errMessage, error);
        addNotificationError({
          message: errMessage,
        });
        navigate('/app', {
          replace: true,
        });
      },
      enabled: Boolean(boardIdParams) && !isTicketPath,
    });

  // Only consider it loading if the query is enabled and actually fetching
  const viewsLoading = Boolean(boardIdParams) && viewsFetching;

  return {
    boards: boardsResponse?.result,
    views: viewsResponse?.result,
    boardsLoading,
    viewsLoading,
  };
};

/**
 *
 * @desc This function is used to get the valid stored board from the local storage
 * @param boards
 * @param localStoredBoardId
 * @returns
 */
const getValidStoredBoard = (
  boards: Board[],
  localStoredBoardId: string
): Board | null => {
  if (!localStoredBoardId) {
    return null;
  }

  return boards.find((board) => board.id === localStoredBoardId) || null;
};

/**
 *
 * @desc This component is being used to guard components under /ticketing
 * it will mainly handle when going to /ticketing/boards
 * to check if the user has access to the board, whether there is boards and views
 * which board and view to render.
 * If the route is not /ticketing/boards, it will return which means just render its child component
 * which is represented by <Outlet />
 */
export const TicketingRoutesGuard = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const navigate = useNavigate();

  const { id, ticketId, viewId = '' } = useParams();

  const {
    boards = [],
    views = { shared: [], personal: [] },
    boardsLoading,
  } = useFetchBoardsAndViews({ isTicketPath: !!ticketId });

  const urlContainAdmin = window.location.pathname.includes('admin');
  const urlContainAllBoards = window.location.pathname.includes('boards/all');
  const urlContainOnboarding = window.location.pathname.includes('onboarding');
  const urlContainOnboardingWizard =
    window.location.pathname.includes('onboarding-wizard');
  const urlContainEmailVerification =
    window.location.pathname.includes('verify-email');
  const isBoardsLoadedButNoData = boards?.length === 0 && !boardsLoading;
  const isRequesterView = useIsRequesterView();

  if (!isAdmin() && !isTicketingAdmin() && isAgent() && urlContainOnboarding) {
    navigate('/app/ticketing/boards/all', { replace: true });
  }

  const hasStoredBoard = getAllRecords('ticketing-selected-board').length > 0;
  const isTicketingFeatureEnabled = useSelector(
    selectIsTicketingFeatureEnabled()
  );

  const hasValidStoredBoard = useMemo(() => {
    if (!hasStoredBoard) {
      return false;
    }

    const storedBoardId = getAllRecords('ticketing-selected-board');
    const storedBoard = getValidStoredBoard(boards, storedBoardId);
    return storedBoard !== null;
  }, [boards, hasStoredBoard]);

  const handleNavigateToOnboarding = useCallback(() => {
    if (
      // Ensure all required states are ready before proceeding
      boardsLoading ||
      views.shared?.length === 0 ||
      // Prevent further redirection on these specific pages
      ticketId ||
      isRequesterView ||
      urlContainAdmin ||
      urlContainAllBoards ||
      urlContainEmailVerification ||
      urlContainOnboardingWizard
    ) {
      return;
    }

    if (!isTicketingFeatureEnabled) {
      navigate('/app/ticketing/not-found', {
        replace: true,
      });
      return;
    }

    const hasBoardsAndViews = boards?.length > 0 && views?.shared?.length > 0;
    if (hasBoardsAndViews) {
      let selectedBoardId = boards?.[0]?.id;
      if (id) {
        selectedBoardId = id;
      } else {
        const storedBoardId = getAllRecords('ticketing-selected-board');
        const storedViewId = getAllRecords('ticketing-selected-view');

        const isBoardValid =
          getValidStoredBoard(boards, storedBoardId) !== null;

        // Use stored values if they exist and are valid
        if (isBoardValid && storedViewId.length) {
          selectedBoardId = storedBoardId;
        }
      }

      // Combine all valid view IDs from both shared and personal lists, filtering out undefined IDs just in case
      const allValidViewIds = [
        ...(views?.shared?.map((v) => v.id).filter(Boolean) || []),
        ...(views?.personal?.map((v) => v.id).filter(Boolean) || []),
      ];

      // Check if the provided viewId exists and is present in the combined list
      const isViewIdValid = viewId && allValidViewIds.includes(viewId);

      // Assign selectedViewId: use viewId if valid, otherwise default to the first shared view
      const selectedViewId = isViewIdValid ? viewId : views?.shared?.[0]?.id;

      if (!isRequesterView) {
        addRecord('ticketing-selected-board', selectedBoardId);
        addRecord('ticketing-selected-view', selectedViewId);
        navigate(
          `/app/ticketing/boards/${selectedBoardId}/views/${selectedViewId}/tickets`,
          { replace: true }
        );
        return;
      }
      return;
    }

    if (!isAdmin()) {
      navigate('/app/ticketing/not-found', {
        replace: true,
      });
    }
  }, [
    boards,
    boardsLoading,
    id,
    isRequesterView,
    isTicketingFeatureEnabled,
    navigate,
    ticketId,
    urlContainAdmin,
    urlContainAllBoards,
    urlContainEmailVerification,
    viewId,
    views?.personal,
    views?.shared,
  ]);

  useEffect(() => {
    handleNavigateToOnboarding();
  }, [handleNavigateToOnboarding]);

  if (boardsLoading) {
    return <Spinner />;
  }

  if (
    isBoardsLoadedButNoData &&
    !urlContainOnboarding &&
    !hasValidStoredBoard &&
    !ticketId &&
    !isRequesterView
  ) {
    // If not an agent and not already on onboarding, redirect to onboarding
    if ((isAdmin() || isTicketingAdmin()) && !urlContainOnboarding) {
      return <Navigate to={ONBOARDING_URL} replace />;
    }
    // If user is an agent and NOT already on the all boards page, redirect there
    if (isAgent() && !urlContainAllBoards) {
      return <Navigate to={ALL_BOARDS_URL} replace />;
    }
  }

  return children;
};
